/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { isArray } from '@ember/array';
import { TrackedArray } from 'tracked-built-ins';
import { get, set, action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isBlank, isEmpty } from '@ember/utils';
import GradingMethod from 'linced-app/enums/grading-method';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { conformJurisdictionalId } from 'linced-app/utils/fields';
import { scaleNumericGrade } from 'linced-app/utils/grades';
import { toCamelCase } from '@linc-technologies/linced-common/utils/strings';
import config from 'linced-app/config/environment';
import moment from 'moment';
import Papa from 'papaparse';
import { observes } from '@ember-decorators/object';
import {
  compact,
  findBy,
  uniq,
  uniqBy,
} from '@linc-technologies/linced-common/utils/arrays';

const ASTTLE_META = [
  'TestID',
  'Test Name',
  'Test Type',
  'Date Modified',
  'Student Master ID',
  'Last Name',
  'First Name',
  'Funding Year Level',
  'Gender',
  'Language',
  'Ethnicity',
];
const NZCER_META = [
  'moe_id',
  'sms_id',
  'surname',
  'preferred_name',
  'year',
  'gender',
  'test_group',
  'test_type',
  'test_num',
  'term',
];

const NAPLAN_META = [
  'Assessment',
  'Participation type',
  'Domain',
  'Scaled score',
  'Proficiency level',
];

const SMART_META = [
  'TestID',
  'TestName',
  'TestType',
  'DateModified',
  'Student_Master_ID',
  'Last Name',
  'First Name',
  'Funding Year Level',
  'Gender',
  'Language',
  'Ethnicity',
  'Overall Score',
  'Overall Level',
  'Deep Score',
  'Deep Level',
  'Surface Level',
  'Surface Score',
  'Attitude',
];

// SMART only ever tests these three subjects.  "TestName" is used to
// determine which subject a row belongs to, since "TestType" values can be
// inconsistent (e.g. "Writing - paper" vs "Writing").
const SMART_SUBJECTS = ['Reading', 'Writing', 'Mathematics'];

const NAPLAN_MODULES = [
  'Writing Scaled Score',
  'Writing Proficiency level',
  'Reading Scaled Score',
  'Reading Proficiency Level',
  'Spelling Scaled Score',
  'Spelling Proficiency Level',
  'Grammar & Punctuation Scaled Score',
  'Grammar & Punctuation Proficiency Level',
  'Numeracy Scaled Score',
  'Numeracy Proficiency Level',
];

export default class AssessmentDataController extends Controller {
  @service
  fetch;

  @service
  session;

  @service
  store;

  GradingMethod = GradingMethod;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @tracked
  currentStep = 0;

  @action
  setCurrentStep(value) {
    this.currentStep = value;
  }

  @tracked
  file = null;

  @action
  setFile(value) {
    this.file = value;
  }

  fileTypes = Object.freeze(['text/csv', '.csv']);

  @tracked
  mode = 'generic';

  naplanProcessedModules = [];

  @tracked
  assessment = null;

  @action
  setAssessment(value) {
    this.assessment = value;
  }

  @computed('model.jurisdictionalIdField')
  get hasJurisdictionalIdField() {
    return !isEmpty(this.model.jurisdictionalIdField);
  }

  @tracked
  processingColumns = false;

  columnsProcessed(results) {
    if (results.data[0][0].substr(0, 16) === 'e-asTTle Tabular') {
      // Remove e-asTTle header row as it's useless and doesn't contain column headers.
      results.data = results.data.slice(1);
      this.mode = 'e-asTTle';
    } else if (
      /_NZCER(Marking|Assist)_/.test(this.file.name) &&
      results.data[0][0] === 'moe_id'
    ) {
      this.mode = 'NZCER';
    } else if (this.file.name.toLowerCase().includes('naplan')) {
      this.mode = 'NAPLAN';
    } else if (this.file.name.toLowerCase().includes('smart')) {
      this.mode = 'SMART';
    } else if (
      this.file.name.toLowerCase().includes('check in') ||
      this.file.name.toLowerCase().includes('check-in')
    ) {
      this.mode = 'CHECK-IN';
    } else {
      this.mode = 'generic';
    }
    this.columns = results.data[0].map((col) => col.trim());
    this.mapJurisdictionalIdField();
    this.mapDateColumn();

    // Don't mark column processing complete yet if we've detected an e-asTTle data file.
    switch (this.mode) {
      case 'e-asTTle':
        // e-asTTle files can contain multiple "tables" of data, and may also contain multiple subjects/assessments.
        // We must therefore preprocess the whole file to make sure that it's actually suitable for import.
        Papa.parse(this.file.data, {
          header: false,
          complete: this.preprocessAsttle.bind(this),
        });
        break;

      case 'NZCER':
        // NZCER files can contain multiple subjects, but we map them to modules within a single "PATs" assessment.
        // We must therefore preprocess the whole file to determine which subjects we're importing.
        Papa.parse(this.file.data, {
          header: false,
          complete: this.preprocessNZCER.bind(this),
        });
        break;
      case 'NAPLAN':
        Papa.parse(this.file.data, {
          header: false,
          complete: this.preprocessNaplan.bind(this),
        });
        break;
      case 'SMART':
        // SMART files contain all subjects in one file, with the subject in
        // the "TestType" column - so we must preprocess the whole file to
        // split the scores/levels out into per-subject columns.
        Papa.parse(this.file.data, {
          header: false,
          complete: this.preprocessSmart.bind(this),
        });
        break;
      case 'CHECK-IN':
        Papa.parse(this.file.data, {
          header: false,
          complete: this.preprocessCheckIn.bind(this),
        });
        break;
      default:
        this.processingColumns = false;
        break;
    }
  }

  @tracked
  columns = [];

  @tracked
  metaColumns = [];

  @tracked
  jurisdictionalIdIndex = -1;

  get jurisdictionalIdFieldMapped() {
    return this.jurisdictionalIdIndex >= 0;
  }

  mapJurisdictionalIdField() {
    let label = this.get('model.jurisdictionalIdField.camelCaseLabel');
    let key = this.get('model.jurisdictionalIdField.key');
    let alternates = [];
    let foundIndex = -1;

    if (!isEmpty(key)) {
      key = toCamelCase(key.split(':').at(-1));
    }

    if (this.jurisdiction === Jurisdiction.NZ_MOE) {
      alternates.push('nsnId'); // NZCER (PATs) / e-asTTle
      alternates.push('nationalStudentNumber'); // NZCER/PATs - Oct 2021.
    }
    if (this.jurisdiction === Jurisdiction.AU_NSW) {
      alternates.push('admissionNumber');
    }

    // Using `any()` rather than `forEach()` so that we stop once we've found a match.
    this.columns.some((col, index) => {
      let camelCased = toCamelCase(col.trim().toLowerCase());
      if (
        camelCased === 'id' ||
        camelCased === label ||
        (!isEmpty(key) && camelCased === key) ||
        alternates.includes(camelCased)
      ) {
        foundIndex = index;
        return true;
      }

      return false;
    });

    this.jurisdictionalIdIndex = foundIndex;
  }

  @tracked dateIndex = -1;

  get dateMapped() {
    return this.dateIndex >= 0;
  }

  mapDateColumn() {
    let search = [
      'date',
      'dateAssessed',
      'assessedOn',
      'assessedOnDate',
      'dateOfAssessment',
      'assessmentDate',
      'dateTested', // e-asTTle / SMART use "Date Tested"
      'completionDate', // Phonics uses "COMPLETION DATE"
    ];
    let foundIndex = -1;

    if (this.mode !== 'e-asTTle') {
      // NZCER / PATs use "Date Modified".
      // e-asTTle files also contain a "Date Modified" column (before "Date
      // Tested"), but there it's file metadata rather than the assessment
      // date - so we must not match it in e-asTTle mode.
      search.push('dateModified');
    }

    // Using `any()` rather than `forEach()` so that we stop once we've found a match.
    this.columns.some((col, index) => {
      let camelCased = toCamelCase(col.trim().toLowerCase());
      if (search.includes(camelCased)) {
        foundIndex = index;
        return true;
      }

      return false;
    });

    this.dateIndex = foundIndex;
  }

  @computed('metaColumns.[]', 'dateIndex', 'jurisdictionalIdIndex')
  get hideColumns() {
    let hide = this.metaColumns.slice();
    if (this.dateIndex > -1) {
      hide.push(this.dateIndex);
    }
    if (this.jurisdictionalIdIndex > -1) {
      hide.push(this.jurisdictionalIdIndex);
    }

    return hide;
  }

  @tracked
  assessmentModules = null;

  @computed('assessmentModules.[]')
  get modules() {
    if (isArray(this.assessmentModules)) {
      return {
        'Assessment Modules': this.assessmentModules.map((module) => {
          let key = isEmpty(module.key) ? module.id : toCamelCase(module.key);
          return {
            key: 'Assessment Modules',
            field: {
              key,
              id: module.id,
              camelCaseLabel: toCamelCase(module.label),
              label: module.label,
              module,
            },
          };
        }),
      };
    }
    return {
      'Assessment Modules': [],
    };
  }

  @computed('assessmentModules.[]', 'preview.data.[]')
  get defaultModuleMap() {
    let destinations = [];
    if (isEmpty(this.columns)) {
      return destinations;
    }

    this.columns.forEach((col, index) => {
      let destination = this.findPotentialMatch(col.trim());

      destination.columnIndex = index;
      destinations.push(destination);
    });

    return destinations;
  }

  @tracked
  moduleMap = [];

  @observes('assessment')
  _assessmentChanged() {
    this.assessmentModules = null;
    this.moduleMap = [];
    if (!isEmpty(this.assessment)) {
      this.modulesLoading = true;
      this.store
        .query('assessments/module', {
          assessmentId: this.assessment.id,
          include: 'gradeSchedules',
        })
        .then((modules) => {
          this.assessmentModules = modules;
          this.modulesLoading = false;
        });
    }
  }

  @computed('mode', 'moduleMap.[]', 'subjectModuleMaps.[]')
  get compactMap() {
    if (this.mode === 'NZCER') {
      // For NZCER, compactMap is only used to determine whether we have any
      // modulesToDataMap.  As such, just smush everything into one flat array.
      let mergedMap = this.subjectModuleMaps.reduce((map, subjectMap) => {
        map.push(...subjectMap.destinations);
        return map;
      }, []);
      return uniqBy(compact(mergedMap), 'field.id');
    }
    return compact(this.moduleMap);
  }

  @computed('compactMap.[]')
  get modulesToDataMap() {
    return this.compactMap.filter(
      (item) =>
        item.field.module.gradeSchedule.awardMethod !== GradingMethod.NUMERIC,
    );
  }

  @computed('modulesToDataMap.[]')
  get needsDataMapping() {
    return this.modulesToDataMap.length > 0;
  }

  processingModules = false;

  @tracked
  dataMap = null;

  @tracked
  dateErrors = [];

  @computed('dateErrors.[]')
  get dateErrorsPreview() {
    return this.dateErrors.slice(0, 10);
  }

  @tracked
  processing = false;

  @tracked
  importError = null;

  @tracked
  importErrorMessage = '';

  // NZCER / PATs
  @tracked
  selectedSubject = 0;

  @action
  setSelectedSubject(value) {
    this.selectedSubject = value;
  }

  @tracked
  subjects = [];

  @tracked
  subjectModuleMaps = [];

  @computed('subjects.[]', 'assessmentModules.[]', 'preview.data.[]')
  get defaultSubjectMaps() {
    return this.subjects.map((subject) => {
      let destinations = [];
      if (isEmpty(this.columns)) {
        return { subject, destinations };
      }

      this.columns.forEach((col, index) => {
        let destination = this.findPotentialMatch(`${subject} ${col.trim()}`);

        destination.columnIndex = index;
        destinations.push(destination);
      });

      return { subject, destinations };
    });
  }

  // findPotentialMatch returns a TrackedArray holding the matching module (if
  // any) for a column - a plain array won't do, since ImportFieldMapper's
  // dropField mutates each column's slot in place (push/splice) via
  // DragSortList, and that needs to be independently reactive.
  findPotentialMatch(column) {
    let colArray = new TrackedArray();
    // Start by searching for a module whose label matches.
    let camelCased = toCamelCase(column.trim().toLowerCase());
    let found = findBy(
      this.modules['Assessment Modules'],
      'field.camelCaseLabel',
      camelCased,
    );
    if (!isEmpty(found)) {
      colArray.push(found);
    }
    return colArray;
  }

  preprocessImpl(results) {
    let values = {};
    let idIndex = this.jurisdictionalIdIndex;
    let modules = this.modulesToDataMap;
    let importData = [];
    // Skip header row
    if (
      this.mode === 'NAPLAN' ||
      this.mode === 'CHECK-IN' ||
      this.mode === 'SMART'
    ) {
      importData = this.importData.slice(1);
    } else {
      importData = results.data.slice(1);
    }
    if (importData.at(-1).length === 1 && isEmpty(importData.at(-1)[0])) {
      // Last row is empty - drop it.
      importData.pop();
    }

    // Initialise an array of values for each module which we need to map data for.
    modules.forEach((map) => {
      values[map.field.id] = [];
    });

    let dateErrors = [];

    importData.forEach((row, rowIndex) => {
      let id = row[idIndex];
      if (isEmpty(id)) {
        // No ID - skip.
        return;
      }

      if (this.dateIndex > -1) {
        // Check that the assessment date is in a format we can understand.
        // Blank dates are allowed (they default to today at import time), but
        // an unparseable date would otherwise be silently replaced with
        // today's date - so surface it to the user to fix instead.
        let rawDate = ('' + (row[this.dateIndex] || '')).trim();
        if (!isEmpty(rawDate) && isEmpty(this.conformAssessmentDate(rawDate))) {
          // +2 to convert to a 1-based row number, counting the header row.
          dateErrors.push({ row: rowIndex + 2, value: rawDate });
        }
      }

      // For each module which requires mapping, push its value into the per-module list.
      modules.forEach((map) => {
        if (
          get(map, 'field.module.gradeSchedule.awardMethod') ===
          GradingMethod.MULTIPLE_SELECT
        ) {
          values[map.field.id].push(
            ...(row[map.index] || '')
              .split(',')
              .map((val) => val.trim())
              .filter((a) => a),
          );
        } else {
          let value = (row[map.index] || '').trim();
          if (!isBlank(value)) {
            // Blank values never create Grade records, so don't ask the user
            // to map them.
            values[map.field.id].push(value);
          }
        }
      });
    });

    modules.forEach((map) => {
      let options = get(map, 'field.module.gradeSchedule.select.values');
      values[map.field.id] = uniq(values[map.field.id]).map((val) => {
        let option = findBy(options, 'value', val);
        let lowerVal = val.toLowerCase();
        if (isEmpty(option)) {
          // Not found as an exact match.  Try lower-case comparison.
          option = options.find((option) => {
            return option.value.toLowerCase() === lowerVal;
          });
        }
        if (isEmpty(option)) {
          // Still not found - try lower-case comparison against option's label.
          option = options.find((option) => {
            return option.label.toLowerCase() === lowerVal;
          });
        }
        if (isEmpty(option)) {
          // Still not found - try lower-case comparison against option's description.
          option = options.find((option) => {
            return option.description.toLowerCase() === lowerVal;
          });
        }

        return {
          import: val,
          module: option,
        };
      });
    });

    this.dataMap = values;
    this.dateErrors = dateErrors;
    this.importData = importData;
    this.preprocessing = false;
  }

  @tracked
  asttleError = false;

  preprocessAsttle(results) {
    let importData = results.data.slice(1); // Skip e-asTTle header row (but not column headers)
    if (importData.at(-1).length === 1 && isEmpty(importData.at(-1)[0])) {
      // Last row is empty - drop it.
      importData.pop();
    }

    for (let i = 0; i < importData.length; i++) {
      if (importData[i][0] === 'Averages') {
        importData.splice(i, 4);
      }
    }

    let subjectIndex = importData[0].indexOf('Test Type');
    if (subjectIndex === -1) {
      this.asttleError =
        'Test Type not found - please check your data file and try again.';
      this.processingColumns = false;
      return;
    }

    // Extract first subject ("test type").
    let firstSubject = importData[1][subjectIndex];
    for (let i = 2; i < importData.length; i++) {
      if (importData[i][subjectIndex] !== firstSubject) {
        this.asttleError =
          'The file you have uploaded contains multiple subjects.  Please upload a single subject at a time.';
        this.processingColumns = false;
        return;
      }
    }

    // Determine which columns contain metadata which we want to ignore / hide.
    let metaColumns = [...this.metaColumns];
    importData[0].forEach((col, index) => {
      if (ASTTLE_META.includes(col.trim())) {
        metaColumns.push(index);
      }
    });
    this.metaColumns = metaColumns;

    this.importData = importData;
    this.processingColumns = false;
    this.asttleError = false;
  }

  getNZCERSubject(raw) {
    let subject = raw.trim().replace(/^PAT_/, '');
    if (subject === 'Comprehension' || subject === 'Vocabulary') {
      subject = `Reading ${subject}`;
    }
    return subject;
  }

  extractSRN(str) {
    if (!isEmpty(str)) {
      const parts = str.split(' - ');
      return parts[1];
    }
  }

  preprocessCheckIn(results) {
    let importData = results.data;
    if (this.dateIndex === -1) {
      importData[0].push('date');
    }
    let nameSRNCol = this.columns.indexOf('Student Name');
    importData[0][nameSRNCol] = 'Admission Number';
    this.columns = importData[0].map((col) => col.trim());

    // map jurisdictionalId & date fields
    this.mapJurisdictionalIdField();
    this.mapDateColumn();

    for (let i = 1; i < importData.length; i++) {
      // extract srn
      importData[i][nameSRNCol] = this.extractSRN(importData[i][nameSRNCol]);
      // push column for date
      importData[i].push('');
    }

    this.importData = importData;
    this.processingColumns = false;
    this.asttleError = false;
  }

  preprocessNaplan(results) {
    let importData = results.data;
    if (this.dateIndex === -1) {
      importData[0].push('date');
    }
    importData[0].push(...NAPLAN_MODULES);
    let nameSRNCol = this.columns.indexOf('Student name - SRN');
    importData[0][nameSRNCol] = 'Admission Number';
    this.columns = importData[0].map((col) => col.trim());

    // map jurisdictionalId & date fields
    this.mapJurisdictionalIdField();
    this.mapDateColumn();

    let domain = this.columns.indexOf('Domain');
    let scaledScore = this.columns.indexOf('Scaled score');
    let proficiencyLevel = this.columns.indexOf('Proficiency level');
    // Process assessment data
    for (let i = 1; i < importData.length; i++) {
      // extract srn
      importData[i][nameSRNCol] = this.extractSRN(importData[i][nameSRNCol]);
      for (let j = 0; j < 11; j++) {
        importData[i].push('');
      }
      switch (importData[i][domain]) {
        case 'Reading': {
          let readingScaledCol = this.columns.indexOf('Reading Scaled Score');
          importData[i][readingScaledCol] = importData[i][scaledScore];
          let readingProficiencyCol = this.columns.indexOf(
            'Reading Proficiency Level',
          );
          importData[i][readingProficiencyCol] =
            importData[i][proficiencyLevel];
          break;
        }
        case 'Spelling': {
          let spellingScaledCol = this.columns.indexOf('Spelling Scaled Score');
          importData[i][spellingScaledCol] = importData[i][scaledScore];
          let spellingProficiencyCol = this.columns.indexOf(
            'Spelling Proficiency Level',
          );
          importData[i][spellingProficiencyCol] =
            importData[i][proficiencyLevel];
          break;
        }
        case 'Grammar & Punctuation': {
          let grammarScaledCol = this.columns.indexOf(
            'Grammar & Punctuation Scaled Score',
          );
          importData[i][grammarScaledCol] = importData[i][scaledScore];
          let grammarProficiencyCol = this.columns.indexOf(
            'Grammar & Punctuation Proficiency Level',
          );
          importData[i][grammarProficiencyCol] =
            importData[i][proficiencyLevel];
          break;
        }
        case 'Numeracy': {
          let numeracyScaledCol = this.columns.indexOf('Numeracy Scaled Score');
          importData[i][numeracyScaledCol] = importData[i][scaledScore];
          let numeracyProficiencyCol = this.columns.indexOf(
            'Numeracy Proficiency Level',
          );
          importData[i][numeracyProficiencyCol] =
            importData[i][proficiencyLevel];
          break;
        }
        case 'Writing': {
          let writingScaledCol = this.columns.indexOf('Writing Scaled Score');
          importData[i][writingScaledCol] = importData[i][scaledScore];
          let writingProficiencyCol = this.columns.indexOf(
            'Writing Proficiency level',
          );
          importData[i][writingProficiencyCol] =
            importData[i][proficiencyLevel];
          break;
        }
      }
    }

    // Determine which columns contain metadata which we want to ignore / hide.
    let metaColumns = [...this.metaColumns];
    importData[0].forEach((col, index) => {
      if (NAPLAN_META.includes(col.trim())) {
        metaColumns.push(index);
      }
    });
    this.metaColumns = metaColumns;

    this.importData = importData;
    this.processingColumns = false;
    this.asttleError = false;
  }

  @tracked
  smartError = false;

  @tracked
  modulesLoading;

  @tracked
  numCreated;

  @tracked
  nzcerError;

  @tracked
  preprocessing;
  @tracked importData;

  preprocessSmart(results) {
    let importData = results.data;
    if (importData.at(-1).length === 1 && isEmpty(importData.at(-1)[0])) {
      // Last row is empty - drop it.
      importData.pop();
    }

    let testNameIndex = this.columns.indexOf('TestName');
    let testTypeIndex = this.columns.indexOf('TestType');
    let overallScoreIndex = this.columns.indexOf('Overall Score');
    let overallLevelIndex = this.columns.indexOf('Overall Level');
    if (
      testNameIndex === -1 ||
      testTypeIndex === -1 ||
      overallScoreIndex === -1 ||
      overallLevelIndex === -1
    ) {
      this.smartError =
        'TestName, TestType, Overall Score and/or Overall Level columns not found - please check your data file and try again.';
      this.processingColumns = false;
      return;
    }

    // SMART files contain one row per student per subject.  The subject is
    // determined from the "TestName" column (e.g. "Y6 Writing April 2026"),
    // since "TestType" values for the same subject can be inconsistent (e.g.
    // "Writing" vs "Writing - paper").  Only keep the subjects actually
    // present in the file.
    let subjects = SMART_SUBJECTS.filter((subject) =>
      importData
        .slice(1)
        .some((row) => this.getSmartSubject(row[testNameIndex]) === subject),
    );

    subjects.forEach((subject) => {
      importData[0].push(
        `${subject} Score`,
        `${subject} Level`,
        `${subject} Test Type`,
      );
    });
    this.columns = importData[0].map((col) => col.trim());

    // map jurisdictionalId & date fields
    this.mapJurisdictionalIdField();
    this.mapDateColumn();

    // Copy each row's score/level/test type into its subject's columns,
    // leaving the other subjects' columns blank so they're skipped at
    // import time.
    for (let i = 1; i < importData.length; i++) {
      let row = importData[i];
      for (let j = 0; j < subjects.length * 3; j++) {
        row.push('');
      }
      let subject = this.getSmartSubject(row[testNameIndex]);
      if (isEmpty(subject)) {
        continue;
      }
      row[this.columns.indexOf(`${subject} Score`)] = this.normalizeSmartValue(
        row[overallScoreIndex],
      );
      row[this.columns.indexOf(`${subject} Level`)] = this.normalizeSmartValue(
        row[overallLevelIndex],
      );
      row[this.columns.indexOf(`${subject} Test Type`)] =
        this.normalizeSmartValue(row[testTypeIndex]);
    }

    // Determine which columns contain metadata which we want to ignore / hide.
    let metaColumns = [...this.metaColumns];
    importData[0].forEach((col, index) => {
      if (SMART_META.includes(col.trim())) {
        metaColumns.push(index);
      }
    });
    this.metaColumns = metaColumns;

    this.importData = importData;
    this.processingColumns = false;
    this.smartError = false;
  }
  normalizeSmartValue(value) {
    // SMART uses "-" for values which aren't applicable to the test type.
    let trimmed = ('' + (value || '')).trim();
    return trimmed === '-' ? '' : trimmed;
  }
  getSmartSubject(testName) {
    let name = ('' + (testName || '')).toLowerCase();
    return (
      SMART_SUBJECTS.find((subject) => name.includes(subject.toLowerCase())) ||
      null
    );
  }

  preprocessNZCER(results) {
    let importData = results.data;
    if (importData.at(-1).length === 1 && isEmpty(importData.at(-1)[0])) {
      // Last row is empty - drop it.
      importData.pop();
    }

    let subjectIndex = this.columns.indexOf('test_type');
    if (subjectIndex === -1) {
      this.nzcerError =
        'Test type (subject) column not found.  Please check your data file and try again.';
      this.processingColumns = false;
      return;
    }

    let subjects = [];
    let maps = [];
    for (let i = 1; i < importData.length; i++) {
      let subject = this.getNZCERSubject(importData[i][subjectIndex]);
      if (!subjects.includes(subject)) {
        subjects.push(subject);
        maps.push({ subject, destinations: [] });
      }
    }

    // Hide metadata columns which we don't care about.
    let metaColumns = [...this.metaColumns];
    this.columns.forEach((col, index) => {
      if (NZCER_META.includes(col.trim())) {
        metaColumns.push(index);
      }
    });
    this.metaColumns = metaColumns;

    this.importData = importData;
    this.subjects = subjects;
    this.selectedSubject = 0;
    this.subjectModuleMaps = maps;
    this.processingColumns = false;
    this.asttleError = false;
  }

  conformAssessmentDate(value) {
    // Assessment dates must be in YYYY-MM-DD, D/M/YYYY, or D-M-YYYY format
    // (day and month may be single- or double-digit, e.g. "7/05/2026" or
    // "7-05-2026"), optionally followed by a time of day (e.g.
    // "21/08/2026 10:26") which is accepted but discarded - only the date is
    // kept.  As with the shared conformDate() util, parsing isn't strict, so
    // trailing text beyond a recognised date is tolerated rather than
    // rejected.  Unlike conformDate(), MM/DD/YYYY is not accepted here:
    // ambiguous dates (day of 12 or less) are always read as day-first,
    // rather than silently mis-parsing month-first data.
    if (isEmpty(value)) {
      return '';
    }
    let parsed = moment(('' + value).trim(), [
      'YYYY-MM-DD',
      'DD/MM/YYYY',
      'D/MM/YYYY',
      'DD/M/YYYY',
      'D/M/YYYY',
      'DD-MM-YYYY',
      'D-MM-YYYY',
      'DD-M-YYYY',
      'D-M-YYYY',
    ]);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
  }

  mapValue(moduleId, value) {
    let map = this.get(`dataMap.${moduleId}`);
    if (isEmpty(map) || !isArray(map)) {
      return null;
    }

    let option = findBy(map, 'import', value);
    if (isEmpty(option)) {
      return null;
    }

    return get(option, 'module.value');
  }

  @action
  processColumns() {
    this.processingColumns = true;
    this.metaColumns = [];
    Papa.parse(this.file.data, {
      header: false,
      preview: 11,
      complete: this.columnsProcessed.bind(this),
    });
  }

  @action
  mappingChanged(map) {
    map.forEach((row, index) => {
      // `row` is a POJO.  No `set()` here.
      if (!isEmpty(row)) {
        row.index = index;
      }
    });
    this.moduleMap = map;
  }

  @action
  subjectMappingChanged(subject, map) {
    map.forEach((row, index) => {
      // `row` is a POJO.  No `set()` here.
      if (!isEmpty(row)) {
        row.index = index;
      }
    });
    let subjectMap = findBy(this.subjectModuleMaps, 'subject', subject);
    set(subjectMap, 'destinations', map);
  }

  @action
  preprocess() {
    this.preprocessing = true;

    // Load all data and pass to the preprocess() function to be checked for data mapping requirements.
    Papa.parse(this.file.data, {
      header: false,
      complete: this.preprocessImpl.bind(this),
    });
  }

  @action
  process() {
    this.processing = true;
    this.importError = false;
    this.importErrorMessage = '';
    this.numCreated = 0;
    let grades = [];
    let assessorId = this.get('session.personId');
    let schoolId = this.get('session.activeSchool');
    let now = moment().unix();

    // Build a map of jurisdictional ID to person ID.
    let learnerMap = this.model.learners.reduce((map, person) => {
      if (!isEmpty(person.jurisdictionalId)) {
        let conformed = conformJurisdictionalId(
          this.jurisdiction,
          '' + person.jurisdictionalId,
        );
        if (!isEmpty(conformed)) {
          map[conformed] = person.id;
        }
      }
      return map;
    }, {});

    let subjectIndex = -1;
    if (this.mode === 'NZCER') {
      subjectIndex = this.columns.indexOf('test_type');
    }

    // We already have all the import data, from the preprocessing step; process it.
    this.importData.forEach((row) => {
      let jurisdictionalId = row[this.jurisdictionalIdIndex];
      if (isEmpty(jurisdictionalId)) {
        // No jurisdictional ID in this row - skip it.
        return;
      }
      jurisdictionalId = conformJurisdictionalId(
        this.jurisdiction,
        jurisdictionalId.trim(),
      );
      let personId = learnerMap[jurisdictionalId];
      if (isEmpty(personId)) {
        // Jurisdictional ID not found in map - skip this row.
        return;
      }

      let columnMap = this.compactMap;
      if (this.mode === 'NZCER') {
        let subject = this.getNZCERSubject(row[subjectIndex]);
        columnMap = compact(
          get(
            findBy(this.subjectModuleMaps, 'subject', subject),
            'destinations',
          ),
        );
      }

      columnMap.forEach((map) => {
        let module = get(map, 'field.module');
        if (isEmpty(module)) {
          // We've somehow lost the module - don't try importing its grades.
          return;
        }
        let rawDate = this.conformAssessmentDate(row[this.dateIndex]);
        let date = moment(); // Default to today in case we don't get a (valid) date.
        if (!isEmpty(rawDate)) {
          // As we've already conformed it, we know it's also valid - so we can just push it straight in.
          date = moment(rawDate, 'YYYY-MM-DD');
        }

        let rawValue = row[map.index];
        if (isBlank(rawValue)) {
          // Blank value - don't create a Grade record.
          return;
        }
        let staninePattern = /stanine/i;
        let isStanine = staninePattern.test(module.label);
        if (
          isStanine &&
          ('' + rawValue).toLowerCase().trim() === 'did not sit'
        ) {
          // This appears to be a Stanine column in an NZCER / PAT assessment import, and this student didn't sit it.
          // Don't create a Grade record, as we don't hav a valid numeric grade/stanine to save.
          return;
        }
        let gradeType = module.get('gradeSchedule.awardMethod');
        let gradeKey = 'selectAwarded';
        let value;
        let grade = {
          personId,
          assessorId,
          schoolId,
          createTime: date.unix(),
          updateTime: now,
          assessmentId: module.get('assessment.id'),
          assessmentModuleId: module.id,
          gradeType,
        };

        switch (gradeType) {
          case GradingMethod.NUMERIC:
            // Numeric grades don't need mapping, but do need scaling.
            grade.numericAwarded = Number(rawValue);
            grades.push(scaleNumericGrade(grade));
            break;

          case GradingMethod.MULTIPLE_SELECT:
            // Multiple select requires array processing as well as data mapping.
            grade.multiSelectAwarded = [];
            rawValue.split(',').forEach((val) => {
              value = this.mapValue(module.id, val.trim());
              if (!isEmpty(value)) {
                grade.multiSelectAwarded.push(value);
              }
            });
            grade.multiSelectAwarded = uniq(grade.multiSelectAwarded);
            if (grade.multiSelectAwarded.length > 0) {
              grades.push(grade);
            }
            break;

          default:
            // Other types of grades don't need scaling, but do need mapping.
            if (gradeType === GradingMethod.ICON_SELECT) {
              gradeKey = 'iconAwarded';
            }

            value = this.mapValue(module.id, rawValue);
            if (!isEmpty(value)) {
              // Don't bother creating empty grades
              grade[gradeKey] = value;
              grades.push(grade);
            }

            break;
        }
      });
    });

    if (grades.length) {
      let options = {
        // Sending `batch` marks these grades as a file import: the server
        // generates a batch id, persists a Batch record, and stamps each
        // created grade's batchId so the import can later be deleted as a unit.
        data: JSON.stringify({
          grades,
          batch: { sourceFilename: this.file.name },
        }),
      };

      this.fetch
        .post(
          `${config.APP.apiHost}/assessments/${config.APP.apiPath}/grades/:bulk`,
          options,
        )
        .then(() => {
          this.numCreated = grades.length;
        })
        .catch((resp) => {
          if (!isEmpty(resp.payload) && !isEmpty(resp.payload.message)) {
            this.importErrorMessage = resp.payload.message;
          } else if (!isEmpty(resp.message)) {
            this.importErrorMessage = resp.message;
          }
          this.importError = true;
        })
        .finally(() => {
          this.processing = false;
        });
    } else {
      // No grades to create.
      this.numCreated = 0;
      this.processing = false;
    }
  }

  @action
  reset() {
    this.setProperties({
      currentStep: 0,
      file: null,
      mode: 'generic',
      processingColumns: false,
      columns: [],
      jurisdictionalIdIndex: -1,
      assessment: null,
      assessmentModules: null,
      moduleMap: [],
      processingModules: false,
      dataMap: null,
      dateErrors: [],
      importData: [],
      processing: false,
      importError: null,
      importErrorMessage: '',
      metaColumns: [],
      smartError: false,
      subjects: [],
      selectedSubject: 0,
      subjectModuleMaps: [],
    });
  }
}
